/** @jsx jsx */

import { VisuallyHidden } from '@balance-web/a11y';
import { jsx } from '@balance-web/core';
import { Flex } from '@balance-web/flex';
import { Stack } from '@balance-web/stack';
import { Text } from '@balance-web/text';
import { useTheme } from '@balance-web/theme';
import type { ReactNode } from 'react';

export type WizardProgressItemProps = {
  number: number;
  title: string;
  subtitle?: string;
  action?: {
    label: string | ReactNode;
    action: () => void;
    disabled?: boolean;
  };
  active: boolean;
  complete: boolean;
};

export const WizardProgressItem = (props: WizardProgressItemProps) => {
  const { number, title, subtitle, action, active, complete } = props;
  const { palette, sizing } = useTheme();

  return (
    <Flex
      alignItems="center"
      flex={1}
      gap="small"
      minHeight={sizing.small}
      minWidth={0}
    >
      <Flex
        alignItems="center"
        borderRadius="large"
        css={{
          background: active ? palette.text.active : palette.text.dim,
        }}
        flexShrink={0}
        height={24}
        justifyContent="center"
        width={24}
      >
        <Text
          css={{
            color: '#fff',
          }}
          size="xsmall"
          weight="semibold"
        >
          {number}
        </Text>
      </Flex>
      <Stack flex={1} minWidth={0}>
        <Text overflowStrategy="nowrap" size="xsmall" weight="semibold">
          {title}
        </Text>
        {!active && (
          <VisuallyHidden>
            {complete ? 'Complete' : 'Incomplete'}
          </VisuallyHidden>
        )}
        <Flex flex={1} gap="small" minWidth={0}>
          {/* ensure that the user can see the subtitle when it's truncated */}
          {subtitle && (
            <Text
              color="dim"
              overflowStrategy="truncate"
              size="xsmall"
              title={subtitle}
            >
              {subtitle}
            </Text>
          )}
          {action && (
            <Text
              aria-disabled={action.disabled}
              as="button"
              color={action.disabled ? 'muted' : 'active'}
              cursor="pointer"
              size="xsmall"
              type="button"
              userSelect="none"
              onClick={action.disabled ? undefined : action.action}
            >
              {action.label}
            </Text>
          )}
        </Flex>
      </Stack>
    </Flex>
  );
};
